// 产品复制 Hook - 复制产品及其所有型号
import { useState, useCallback } from 'react';
import { useProductStore } from '@/poster/stores/product-store';
import type { Product } from '@/poster/types';

interface UseProductCopyReturn {
  copyProduct: (product: Product) => Promise<Product | null>;
  isCopying: boolean;
}

/**
 * 产品复制 Hook
 * 复制产品时会同时复制所有关联的型号
 */
export function useProductCopy(): UseProductCopyReturn {
  const [isCopying, setIsCopying] = useState(false);
  const { createProduct, createProductModel, fetchProducts } = useProductStore();

  const copyProduct = useCallback(async (product: Product): Promise<Product | null> => {
    if (isCopying) return null;
    
    setIsCopying(true);
    try {
      // 1. 准备产品数据（不包含 id, models, 时间戳）
      const productData = {
        name: `${product.name} (副本)`,
        name_en: product.name_en,
        category: product.category,
        series_name: product.series_name,
        image_url: product.image_url,
        image_storage_key: product.image_storage_key,
        pitch_range: product.pitch_range,
        size_specs: product.size_specs,
        process_type: product.process_type,
        features: product.features,
        slogan: product.slogan,
        is_featured: product.is_featured,
        sort_order: (product.sort_order || 0) + 1, // 排序在原产品后面
      };

      // 2. 创建新产品
      const newProduct = await createProduct(productData);

      // 3. 复制所有型号（如果有）
      if (product.models && product.models.length > 0) {
        const modelPromises = product.models.map((model) =>
          createProductModel({
            product_id: newProduct.id,
            model_name: model.model_name,
            pitch: model.pitch,
            cabinet_size: model.cabinet_size,
            weight: model.weight,
            power_consumption: model.power_consumption,
            is_default: model.is_default,
            sort_order: model.sort_order,
          })
        );
        await Promise.all(modelPromises);
        
        // 刷新产品列表以获取更新后的型号数据
        await fetchProducts();
      }

      return newProduct;
    } catch (error) {
      console.error('Error copying product:', error);
      throw error;
    } finally {
      setIsCopying(false);
    }
  }, [isCopying, createProduct, createProductModel, fetchProducts]);

  return { copyProduct, isCopying };
}
